// Course Package Import — validator (Step 2).
// Pure, browser-only. Takes a ReadResult (packageReader) and produces a plan:
// per-course / topic / block statuses and messages, counts and importability.
// No network, nothing saved. Rules: PRD sections 3–6.

import { strFromU8 } from 'fflate';
import { csvToRawQuestions, normaliseQuestion, QUIZ_QUESTION_TYPES, ALL_QUESTION_TYPES } from './questionsCsv';

export const PACKAGE_TYPES = {
  Video: 'Video', Reading: 'RICHTEXT', Slides: 'PPT', LiveClassSlides: 'LiveClassPPT',
  LiveSession: 'LiveSession', Quiz: 'Quiz', Assignment: 'Assignment', GuidedLab: 'Lab',
  AILab: 'Lab', LeanCanvas: 'LeanCanvas', StakeholderSim: 'StakeholderSim', IndustryLab: 'IndustryLab',
};

const FOLDER_EXTS = {
  content: ['md', 'markdown', 'html', 'htm', 'txt'],
  slides: ['pptx', 'ppt', 'pdf'],
  documents: ['pdf', 'docx'],
  questions: ['json', 'csv', 'py', 'js', 'sql', 'java', 'cpp', 'c', 'html', 'css', 'bash', 'txt'],
  datasets: ['csv', 'json'],
  labs: ['json'],
  images: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'],
};
const VIDEO_EXTS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'];
const ARCHIVE_EXTS = ['zip', 'rar', '7z'];
const MAX_FILE_BYTES = 50 * 1024 * 1024;

const FIELDS_WHITELIST = {
  course: ['shortTitle', 'showShortTitleOverlay', 'sequentialUnlock', 'certificateEnabled', 'xpReward', 'lockdownSettings', 'proctoringSettings', 'scheduledPublishAt'],
  topic: ['skillIds', 'xpReward', 'scheduledPublishAt'],
  block: ['gradingMode', 'grading'],
};
const DIFFICULTIES = ['Beginner', 'Intermediate', 'Advanced'];
const KEY_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

const ext = (p) => (String(p).split('.').pop() || '').toLowerCase();
const folderOf = (p) => String(p).split('/')[0];
const isNoteKey = (k) => typeof k === 'string' && k.startsWith('_');
const pathUnsafe = (p) => typeof p !== 'string' || p.includes('..') || p.startsWith('/');

// Deep scan for an unfilled {{FILL: placeholder, skipping note keys (_-prefixed).
function hasPlaceholder(val) {
  if (val == null) return false;
  if (typeof val === 'string') return val.includes('{{FILL:');
  if (Array.isArray(val)) return val.some(hasPlaceholder);
  if (typeof val === 'object') {
    return Object.entries(val).some(([k, v]) => !isNoteKey(k) && hasPlaceholder(v));
  }
  return false;
}

// Strip note keys recursively (used so counts/pass-through ignore _ keys).
export function stripNotes(val) {
  if (Array.isArray(val)) return val.map(stripNotes);
  if (val && typeof val === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(val)) if (!isNoteKey(k)) out[k] = stripNotes(v);
    return out;
  }
  return val;
}

function msg(level, code, text) { return { level, code, text }; }

export function validatePackage(readResult) {
  const files = readResult.files || new Map();
  const index = readResult.index || {};
  const referenced = new Set(['course.json', 'README.md', 'schema/course.schema.json']);
  const seenKeys = new Map(); // key -> where
  const plan = {
    fatal: null,
    course: { title: '', fields: {}, messages: [], status: 'ready', thumbnailPath: null },
    topics: [],
    ignored: [...(readResult.ignored || [])],
    counts: { byType: {}, files: 0, totalBytes: readResult.totalBytes || 0, ready: 0, warning: 0, error: 0 },
    importableBlocks: 0,
  };

  // helpers bound to this package
  const fileExists = (p) => files.has(p);
  const checkFile = (p, folder, messages, { kind = 'file' } = {}) => {
    referenced.add(p);
    if (pathUnsafe(p)) { messages.push(msg('error', 'FILE_TYPE', `unsafe path: ${p}`)); return false; }
    if (VIDEO_EXTS.includes(ext(p))) { messages.push(msg('error', 'VIDEO_FILE', 'videos must be links, not files')); return false; }
    if (ARCHIVE_EXTS.includes(ext(p))) { messages.push(msg('error', 'FILE_TYPE', `${p}: archives are not allowed`)); return false; }
    if (!fileExists(p)) { messages.push(msg('error', 'FILE_MISSING', `${p} is referenced but not in the zip`)); return false; }
    const bytes = files.get(p);
    if (bytes.length > MAX_FILE_BYTES) { messages.push(msg('error', 'FILE_TOO_BIG', `${p} is over 50 MB`)); return false; }
    const accepted = FOLDER_EXTS[folder] || [];
    if (accepted.length && !accepted.includes(ext(p))) {
      messages.push(msg('error', 'FILE_TYPE', `${p}: .${ext(p)} isn't accepted here (accepted: ${accepted.join(', ')})`));
      return false;
    }
    return true;
  };

  // ---- format gate (fatal) ----
  if (index.format !== 'elevante-course-package' || index.formatVersion !== 1) {
    plan.fatal = { code: 'PKG_BAD_FORMAT', message: 'format/formatVersion not supported' };
    return plan;
  }

  // ---- course ----
  const course = index.course || {};
  plan.course.title = course.title || '';
  if (!course.title || !String(course.title).trim()) {
    plan.fatal = { code: 'API_ERROR', message: 'course.title is required' };
    return plan;
  }
  if (hasPlaceholder(course.title)) {
    plan.fatal = { code: 'PLACEHOLDER', message: 'unfilled {{FILL:}} in course title' };
    plan.course.status = 'error';
    plan.course.messages.push(msg('error', 'PLACEHOLDER', 'course title still has {{FILL:}}'));
    // keep going so the preview can still list topics/blocks as errors
  }
  if (course.difficulty && !hasPlaceholder(course.difficulty) && !DIFFICULTIES.includes(course.difficulty)) {
    plan.course.messages.push(msg('warning', 'FIELD_UNKNOWN', `difficulty "${course.difficulty}" not recognised, defaults to Beginner`));
  }
  if (course.thumbnail && !hasPlaceholder(course.thumbnail)) {
    const ok = checkFile(course.thumbnail, 'images', plan.course.messages);
    if (ok) plan.course.thumbnailPath = course.thumbnail;
  }
  // course.fields whitelist
  const cf = course.fields || {};
  for (const k of Object.keys(cf)) {
    if (isNoteKey(k)) continue;
    if (FIELDS_WHITELIST.course.includes(k)) plan.course.fields[k] = cf[k];
    else plan.course.messages.push(msg('warning', 'FIELD_UNKNOWN', `course field "${k}" skipped`));
  }
  if (plan.course.status !== 'error') {
    plan.course.status = plan.course.messages.some((m) => m.level === 'error') ? 'error'
      : (plan.course.messages.some((m) => m.level === 'warning') ? 'warning' : 'ready');
  }

  // ---- topics + blocks ----
  const topics = Array.isArray(index.topics) ? index.topics : [];
  for (const t of topics) {
    const tMsgs = [];
    const tkey = t.key;
    if (!tkey || !KEY_RE.test(String(tkey))) tMsgs.push(msg('error', 'API_ERROR', `topic key "${tkey}" is missing or invalid`));
    else if (seenKeys.has(tkey)) tMsgs.push(msg('error', 'KEY_DUP', `key "${tkey}" is used twice`));
    else seenKeys.set(tkey, 'topic');
    if (!t.name || !String(t.name).trim()) tMsgs.push(msg('error', 'API_ERROR', 'topic name is required'));
    if (hasPlaceholder(t.name)) tMsgs.push(msg('error', 'PLACEHOLDER', 'topic name still has {{FILL:}}'));
    // topic.fields whitelist
    const tf = t.fields || {};
    for (const k of Object.keys(tf)) {
      if (isNoteKey(k)) continue;
      if (!FIELDS_WHITELIST.topic.includes(k)) tMsgs.push(msg('warning', 'FIELD_UNKNOWN', `topic field "${k}" skipped`));
    }

    const topicPlan = {
      key: tkey, name: t.name || '(unnamed topic)',
      status: 'ready', messages: tMsgs, blocks: [],
    };

    const blocks = Array.isArray(t.blocks) ? t.blocks : [];
    for (const b of blocks) {
      const bMsgs = [];
      const bkey = b.key;
      const type = b.type;
      if (!bkey || !KEY_RE.test(String(bkey))) bMsgs.push(msg('error', 'API_ERROR', `block key "${bkey}" is missing or invalid`));
      else if (seenKeys.has(bkey)) bMsgs.push(msg('error', 'KEY_DUP', `key "${bkey}" is used twice`));
      else seenKeys.set(bkey, 'block');
      if (!type || !PACKAGE_TYPES[type]) bMsgs.push(msg('error', 'API_ERROR', `unknown block type "${type}"`));
      if (!b.title || !String(b.title).trim()) bMsgs.push(msg('error', 'API_ERROR', 'block title is required'));

      // Placeholder scan for the whole block (minus note keys). Any unfilled
      // {{FILL:}} makes the block an error (PRD section 3).
      if (hasPlaceholder(b)) bMsgs.push(msg('error', 'PLACEHOLDER', 'this block still has unfilled {{FILL:}} values'));

      // block.fields whitelist
      const bf = b.fields || {};
      for (const k of Object.keys(bf)) {
        if (isNoteKey(k)) continue;
        if (!FIELDS_WHITELIST.block.includes(k)) bMsgs.push(msg('warning', 'FIELD_UNKNOWN', `block field "${k}" skipped`));
      }

      // Per-type checks only run when there is no placeholder (otherwise the
      // placeholder values would produce noisy, redundant errors).
      if (type && PACKAGE_TYPES[type] && !hasPlaceholder(b)) {
        validateBlockType(type, b, { checkFile, files, bMsgs });
      }

      const status = bMsgs.some((m) => m.level === 'error') ? 'error'
        : (bMsgs.some((m) => m.level === 'warning') ? 'warning' : 'ready');
      topicPlan.blocks.push({ key: bkey, type, title: b.title || '(untitled)', status, messages: bMsgs });
      plan.counts.byType[type] = (plan.counts.byType[type] || 0) + 1;
      plan.counts[status] += 1;
    }

    topicPlan.status = tMsgs.some((m) => m.level === 'error') ? 'error'
      : (tMsgs.some((m) => m.level === 'warning') ? 'warning' : 'ready');
    plan.topics.push(topicPlan);
  }

  // importable blocks = block not error, topic not error, no fatal
  if (!plan.fatal) {
    for (const tp of plan.topics) {
      if (tp.status === 'error') continue;
      for (const bl of tp.blocks) if (bl.status !== 'error') plan.importableBlocks += 1;
    }
  }

  // unreferenced files -> ignored list
  for (const p of files.keys()) {
    if (!referenced.has(p) && !plan.ignored.includes(p)) plan.ignored.push(p);
  }
  plan.counts.files = files.size;
  return plan;
}

function validateBlockType(type, b, { checkFile, files, bMsgs }) {
  switch (type) {
    case 'Video': {
      if (b.file) { bMsgs.push(msg('error', 'VIDEO_FILE', 'videos must be links, not files')); break; }
      const url = (b.video && b.video.url) || '';
      if (!/^https:\/\//i.test(url)) { bMsgs.push(msg('error', 'VIDEO_LINK', 'video.url must be an https link')); break; }
      const yt = /youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\//i.test(url);
      const vimeo = /vimeo\.com\/\d+/i.test(url);
      const direct = /\.(mp4|webm|mov)(\?|$)/i.test(url);
      if (!yt && !vimeo && !direct) {
        bMsgs.push(msg('error', 'VIDEO_LINK', "this link can't play in the Video block. Use YouTube, Vimeo or a direct .mp4/.webm/.mov link"));
      }
      break;
    }
    case 'Reading': {
      const src = b.source || {};
      const keys = ['file', 'markdown', 'html', 'pdf'].filter((k) => src[k] != null);
      if (keys.length !== 1) { bMsgs.push(msg('error', 'API_ERROR', 'Reading needs exactly one source (file | markdown | html | pdf)')); break; }
      if (src.file) checkFile(src.file, 'content', bMsgs);
      if (src.pdf) checkFile(src.pdf, 'documents', bMsgs);
      break;
    }
    case 'Slides':
    case 'LiveClassSlides': {
      if (!b.file) { bMsgs.push(msg('error', 'API_ERROR', 'a deck file is required')); break; }
      checkFile(b.file, 'slides', bMsgs);
      if (type === 'LiveClassSlides' && b.trainerGuide && b.trainerGuide.file) {
        checkFile(b.trainerGuide.file, 'content', bMsgs);
      }
      break;
    }
    case 'LiveSession': {
      if (b.file) bMsgs.push(msg('error', 'LIVE_FILE', 'a live class deck belongs in a LiveClassSlides block'));
      break;
    }
    case 'Quiz':
    case 'Assignment': {
      validateQuestions(type, b, { files, bMsgs });
      if (type === 'Assignment' && Array.isArray(b.attachments)) {
        for (const a of b.attachments) checkFile(a, 'documents', bMsgs);
      }
      break;
    }
    case 'GuidedLab':
    case 'AILab': {
      for (const ds of (b.datasets || [])) if (ds && ds.file) checkFile(ds.file, 'datasets', bMsgs);
      if (type === 'AILab') {
        if (b.workflow && b.workflow.file) checkFile(b.workflow.file, 'labs', bMsgs);
        if (b.experience && b.experience.file) checkFile(b.experience.file, 'labs', bMsgs);
      }
      break;
    }
    case 'StakeholderSim': {
      const mt = b.content && b.content.maxTurns;
      if (mt != null && (!Number.isFinite(Number(mt)) || mt < 1 || mt > 50)) {
        bMsgs.push(msg('warning', 'FIELD_UNKNOWN', 'maxTurns should be 1–50'));
      }
      break;
    }
    case 'IndustryLab': {
      const slug = b.content && b.content.labSlug;
      if (!slug) bMsgs.push(msg('error', 'API_ERROR', 'content.labSlug is required'));
      break;
    }
    default:
      break;
  }
}

function validateQuestions(type, b, { files, bMsgs }) {
  const allowed = type === 'Quiz' ? QUIZ_QUESTION_TYPES : ALL_QUESTION_TYPES;
  let rawList = null;
  const q = b.questions;
  if (Array.isArray(q)) {
    rawList = q;
  } else if (q && q.file) {
    if (!files.has(q.file)) { bMsgs.push(msg('error', 'FILE_MISSING', `${q.file} is referenced but not in the zip`)); return; }
    const text = strFromU8(files.get(q.file));
    if (/\.csv$/i.test(q.file)) {
      const { raw, error } = csvToRawQuestions(text);
      if (error) { bMsgs.push(msg('warning', 'Q_INVALID', error)); return; }
      rawList = raw;
    } else {
      try { rawList = JSON.parse(text); } catch (e) { bMsgs.push(msg('error', 'Q_INVALID', `${q.file} is not valid JSON`)); return; }
    }
  } else {
    bMsgs.push(msg('warning', 'Q_INVALID', 'no questions given'));
    return;
  }
  if (!Array.isArray(rawList)) { bMsgs.push(msg('error', 'Q_INVALID', 'questions must be a list')); return; }
  const resolveCode = (ref) => (files.has(ref) ? strFromU8(files.get(ref)) : undefined);
  let valid = 0;
  rawList.forEach((rawQ, i) => {
    const { error } = normaliseQuestion(rawQ, { allowedTypes: allowed, resolveCode });
    if (error) bMsgs.push(msg('warning', 'Q_INVALID', `question ${i + 1}: ${error}`));
    else valid += 1;
  });
  if (valid === 0) bMsgs.push(msg('warning', 'Q_INVALID', 'no valid questions'));
}
